import { useLanguageStore } from "@/lib/language-store"
import { useAuthStore } from "@/lib/auth-store"
import { CantileverBOMSummary } from "@/lib/cantilever-bom"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

interface CantileverBOMDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  bom: CantileverBOMSummary | null
}

export function CantileverBOMDialog({ open, onOpenChange, bom }: CantileverBOMDialogProps) {
  const { getTranslation } = useLanguageStore()
  const { user } = useAuthStore()
  const showPrices = !!user

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-auto">
        <DialogHeader>
          <DialogTitle>{getTranslation("bom.title")}</DialogTitle>
        </DialogHeader>

        {!bom ? (
          <p className="text-sm text-gray-500">{getTranslation("bom.loading")}</p>
        ) : (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left p-3 font-medium">{getTranslation("bom.material.id")}</th>
                    <th className="text-left p-3 font-medium">{getTranslation("bom.description")}</th>
                    <th className="text-center p-3 font-medium">{getTranslation("bom.unit")}</th>
                    <th className="text-right p-3 font-medium">{getTranslation("bom.qty")}</th>
                    {showPrices && <th className="text-right p-3 font-medium">{getTranslation("bom.unit.price")}</th>}
                    {showPrices && <th className="text-right p-3 font-medium">{getTranslation("bom.total")}</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {bom.items.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="p-3 font-mono text-xs">{item.materialId}</td>
                      <td className="p-3">
                        <div className="flex flex-col">
                          <span className="font-medium">{item.description}</span>
                          <span className="text-xs text-gray-500">{item.category}</span>
                        </div>
                      </td>
                      <td className="text-center p-3">{item.unit}</td>
                      <td className="text-right p-3 font-semibold">{item.quantity}</td>
                      {showPrices && <td className="text-right p-3">€{item.unitPrice.toFixed(2)}</td>}
                      {showPrices && (
                        <td className="text-right p-3 font-semibold">€{item.totalPrice.toFixed(2)}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
                {showPrices && (
                  <tfoot className="bg-gray-50 font-bold border-t border-gray-200">
                    <tr>
                      <td colSpan={4} className="p-3 text-right">{getTranslation("bom.total")}:</td>
                      <td className="p-3 text-right text-green-600" colSpan={2}>
                        €{bom.totalPrice.toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
